import { Component, Input, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { StorageService } from 'app/modules/auth/services/storage.service';
import { formations } from 'app/modules/admin/lineup/formations';
@Component({
    selector: 'app-lineup',
    standalone: true,
    imports: [CommonModule],
    templateUrl: './lineup.component.html',
    styleUrls: ['./lineup.component.scss'],
})
export class LineupComponent implements OnInit {
    @Input() match: any;
    @Input() lastValidLineup: any = null;
    @Input() readOnlyMode: boolean = false;
    selectedPlayer = signal<any | null>(null);
    selectedTeam: 'team1' | 'team2' = 'team1';
    substitutePlayers = signal<any[]>([]);
    fieldImage = signal<string>('');
    fieldImageMobile = signal<string>('');
    constructor(private _storageService: StorageService) {}
    ngOnInit(): void {
        this.updateFieldImage();
        this.prepareSubstitutes();
    }

    updateFieldImage(): void {
        const sport = this._storageService.getClubData()?.sport_name;
        switch (sport.toLowerCase()) {
            case 'football':
                this.fieldImage.set(
                    'assets/images/terrain/terrain-foot-desktop1.jpg'
                );
                this.fieldImageMobile.set('assets/images/terrain/FOOT.jpg');
                break;
            case 'handball':
                this.fieldImage.set(
                    'assets/images/terrain/terrain-hand-desktop.jpg'
                );
                this.fieldImageMobile.set(
                    'assets/images/terrain/terrain-hand-desktop.jpg'
                );
                break;
            case 'basketball':
                this.fieldImage.set(
                    'assets/images/terrain/terrain-bask-desktop.jpg'
                );
                this.fieldImageMobile.set('assets/images/terrain/BASKET.jpg');
                break;
            case 'rugby':
                this.fieldImage.set(
                    'assets/images/terrain/terrain-rugby-desktop.jpg'
                );
                this.fieldImageMobile.set(
                    'assets/images/terrain/terrain-rugby-mobile.jpg'
                );
                break;
            default:
                this.fieldImage.set(
                    'assets/images/terrain/terrain-foot-desktop1.jpg'
                );
                this.fieldImageMobile.set('assets/images/terrain/FOOT.jpg');
        }
    }

    get players() {
        return this.match?.teams?.[this.selectedTeam]?.players || [];
    }
    selectPlayer(player: any) {
        if (player && player.id) {
            this.selectedPlayer.set(player);
        } else {
            console.warn('Tentative de sélection d’un joueur invalide', player);
        }
    }
    prepareSubstitutes() {
        const raw = this.match?.lineup?.substitutes;
        let ids: string[] = [];

        try {
            ids = typeof raw === 'string' ? JSON.parse(raw) : raw;
        } catch (err) {
            console.warn('Failed to parse substitutes');
        }

        const teamPlayers =
            this.match?.teams?.[this.selectedTeam]?.players || [];

        const expelledPlayerIds = (this.match?.liveEvents || [])
            .filter(
                (e) => e.player?.id && this.getCardType(e.player.id) === 'red'
            )
            .map((e) => e.player.id);

        const uniqueSubIds = Array.from(new Set(ids)).filter(
            (id) => !expelledPlayerIds.includes(id)
        );

        const result = uniqueSubIds
            .map((id: string) => teamPlayers.find((p) => p.id === id))
            .filter(Boolean);

        this.substitutePlayers.set(result);
    }

    getExpulsionType(playerId: string): 'red_card' | 'two_yellow_cards' | null {
        const events = (this.match?.liveEvents || []).filter(
            (e) => e.player?.id === playerId
        );

        const redCards = events.filter(
            (e) => e.type?.toLowerCase() === 'red_card'
        );
        const yellowCards = events.filter(
            (e) => e.type?.toLowerCase() === 'yellow_card'
        );

        if (redCards.length > 0) return 'red_card';
        if (yellowCards.length >= 2) return 'two_yellow_cards';

        return null;
    }

    getPlayerPosition(index: number): {
        top: string;
        left?: string;
        right?: string;
        transform?: string;
    } | null {
        const tactic = this.match?.lineup?.formation;
        const team = this.selectedTeam;

        // Récupérer le joueur positionné
        const playerData = this.match?.lineup?.starting_players?.[index];

        if (playerData?.top && playerData?.left) {
            return {
                top: playerData.top,
                left: playerData.left,
            };
        }

        // Fallback : position par défaut selon la tactique
        if (!tactic || !formations.hasOwnProperty(tactic)) return null;
        const positions = formations[tactic].positions;
        if (index >= positions.length) return null;

        return positions[index];
    }

    get positionedPlayers() {
        const lineup = this.match?.lineup;
        const teamPlayers =
            this.match?.teams?.[this.selectedTeam]?.players || [];

        if (!lineup?.starting_players || lineup.starting_players.length === 0) {
            return [];
        }

        return lineup.starting_players.map((pos: any) => {
            const player = teamPlayers.find((p: any) => p.id === pos.playerId);

            // Si le joueur a des top/left sauvegardés, on les prend directement
            const position = {
                top: pos.top || '50%',
                left: pos.left || '65%',
            };

            return {
                ...pos,
                id: pos.playerId,
                firstName: player?.firstName ?? '',
                lastName: player?.lastName ?? '',
                jerseyNumber: player?.jerseyNumber ?? '',
                ...position,
            };
        });
    }

    getInitials(player: any): string {
        if (!player || (!player.firstName && !player.lastName)) return '?';

        const first = player.firstName?.trim()?.charAt(0) || '';
        const last = player.lastName?.trim()?.charAt(0) || '';
        return (first + last).toUpperCase();
    }

    getTeamName(): string {
        return this.match?.teams?.[this.selectedTeam]?.name || 'Équipe';
    }

    switchTeam(team: 'team1' | 'team2') {
        this.selectedTeam = team;
    }

    getGoals(playerId: string): any[] {
        if (!playerId) {
            return [];
        }

        const goals =
            this.match?.liveEvents?.filter(
                (e: any) =>
                    e.type?.toLowerCase() === 'goal' &&
                    e.player?.id === playerId
            ) || [];

        return goals;
    }
    getGoalCount(playerId: string): number {
        return this.getGoals(playerId).length;
    }

    hasCard(playerId: string): boolean {
        return this.hasYellowCard(playerId) || this.hasRedCard(playerId);
    }
    hasYellowCard(playerId: string): boolean {
        return this.match?.liveEvents?.some(
            (e: any) =>
                e.player?.id === playerId &&
                e.type.toLowerCase() === 'yellow_card'
        );
    }

    hasRedCard(playerId: string): boolean {
        return this.match?.liveEvents?.some(
            (e: any) =>
                e.player?.id === playerId && e.type.toLowerCase() === 'red_card'
        );
    }
    getYellowCardCount(playerId: string): number {
        return (this.match?.liveEvents || []).filter(
            (e) =>
                e.type?.toLowerCase() === 'yellow_card' &&
                e.player?.id === playerId
        ).length;
    }
    getCardType(playerId: string): 'none' | 'yellow' | 'red' {
        const events =
            this.match?.liveEvents?.filter((e) => e.player?.id === playerId) ||
            [];

        const yellowCards = events.filter(
            (e) => e.type?.toLowerCase() === 'yellow_card'
        ).length;
        const redCards = events.filter(
            (e) => e.type?.toLowerCase() === 'red_card'
        ).length;

        if (redCards > 0 || yellowCards >= 2) {
            return 'red';
        }

        if (yellowCards === 1) {
            return 'yellow';
        }

        return 'none';
    }
    getRedCardedPlayers(): any[] {
        const teamPlayers =
            this.match?.teams?.[this.selectedTeam]?.players || [];

        const redCardedIds = (this.match?.liveEvents || [])
            .filter(
                (e) => e.player?.id && this.getCardType(e.player.id) === 'red'
            )
            .map((e) => e.player.id);

        const uniqueIds = Array.from(new Set(redCardedIds));

        return uniqueIds
            .map((id) => teamPlayers.find((p) => p.id === id))
            .filter(Boolean);
    }
}
